/**
 * LineItemsTable Component
 * 
 * Editable table of invoice line items.
 * Extracted from InvoiceForm for better SRP and readability.
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

import { Product } from 'types';
import { ProductAutocomplete } from '../common';
import { formatAmount } from '../../utils/invoiceUtils';
import { FormLineItem, calculateLineTotal } from 'app/hooks';

interface LineItemsTableProps {
  /** Current line items */
  lineItems: FormLineItem[];
  /** Validation error message (if any) */
  validationError?: string;
  /** Handler for updating a line item's product */
  onUpdateProduct: (index: number, product: Product | null) => void;
  /** Handler for updating a line item's quantity */
  onUpdateQuantity: (index: number, quantity: number) => void;
  /** Handler for removing a line item */
  onRemoveLine: (index: number) => void;
  /** Handler for adding a new line item */
  onAddLine: () => void;
}

const LineItemsTable: React.FC<LineItemsTableProps> = ({
  lineItems,
  validationError,
  onUpdateProduct,
  onUpdateQuantity,
  onRemoveLine,
  onAddLine,
}) => {
  return (
    <div className="invoice-lines-section invoice-lines-form">
      {validationError && (
        <div className="validation-error-banner">
          {validationError}
        </div>
      )}
      <table className="invoice-lines-table">
        <thead>
          <tr>
            <th className="line-col-num"></th>
            <th className="line-col-product">Product</th>
            <th className="line-col-qty">Qty</th>
            <th className="line-col-price">Unit Price (EUR)</th>
            <th className="line-col-total">Total (EUR)</th>
            <th className="line-col-vat">VAT</th>
            <th className="line-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          {lineItems.map((item, index) => (
            <tr key={item.clientId}>
              <td className="line-col-num">{index + 1}</td>
              <td className="line-col-product">
                <ProductAutocomplete
                  value={item.product}
                  onChange={(product) => onUpdateProduct(index, product)}
                />
              </td>
              <td className="line-col-qty">
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={item.quantity}
                  onChange={(e) => onUpdateQuantity(index, parseInt(e.target.value, 10) || 1)}
                  min="1"
                  style={{ width: '70px', textAlign: 'center' }}
                />
              </td>
              <td className="line-col-price">
                {item.product ? formatAmount(parseFloat(item.product.unit_price_without_tax)).replace('€', '').trim() : '—'}
              </td>
              <td className="line-col-total">
                {item.product ? formatAmount(calculateLineTotal(item)).replace('€', '').trim() : '—'}
              </td>
              <td className="line-col-vat">
                {item.product ? `${item.product.vat_rate} %` : '—'}
              </td>
              <td className="line-col-actions">
                <button
                  type="button"
                  className="action-btn"
                  onClick={() => onRemoveLine(index)}
                  title="Remove"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </td>
            </tr>
          ))}
          {lineItems.length === 0 && (
            <tr>
              <td colSpan={7} className="empty-lines">
                No line items added yet
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Add Item Button */}
      <div className="invoice-form-add-item">
        <button
          type="button"
          className="btn-add-item"
          onClick={onAddLine}
        >
          <FontAwesomeIcon icon={faPlus} />{' '}
          Add Item
        </button>
      </div>
    </div>
  );
};

export default LineItemsTable;
